import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useWealthboxClientLink } from '../../../../../api/wealthbox'
import { postNamedQuery } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'

// Wealthbox household-title enum. Verified from the live dev.wealthbox.com
// docs (Aug 2026) under the POST /v1/households/{id}/members endpoint - the
// `title` request attribute is a fixed enum, not firm-defined relationship
// types. The original SMT-1356 ticket text said titles would come from a
// `/v1/relationship_types` endpoint that doesn't actually exist.
export const HOUSEHOLD_TITLES = [
  'Head',
  'Spouse',
  'Partner',
  'Child',
  'Grandchild',
  'Parent',
  'Grandparent',
  'Sibling',
  'Other Dependent'
]

// Data hook for the wealthbox-contact-info component.
//
// Architecture (verified against live Wealthbox docs):
//   - We resolve the Summit clientId to a Wealthbox contact id (the primary
//     contact for this client).
//   - That contact's GET response carries `household: { id, name, title,
//     members: [{id, first_name, last_name, title, type}] }`.
//   - We render one button per `household.members` entry (including the
//     primary). No separate relationships fetch needed.
//   - If the primary contact has no household (a standalone Person or an
//     Organization/Trust), we just render the primary button.
//
// What the SMT-1356 ticket text described - `/v1/contacts/{id}/relationships`
// and `/v1/relationship_types` endpoints - do NOT exist on Wealthbox. The
// `household.members` model is the actual API surface for family relationships.
export default function useWealthboxContactInfo (config = {}) {
  const { clientId, userId } = useAppContext()
  const link = useWealthboxClientLink(clientId)
  const primaryContactId = link.data?.externalId ?? null

  const primaryQuery = useQuery({
    queryKey: [QUERY_KEYS.wealthboxDomainQuery, 'contact-info-primary', userId, clientId, primaryContactId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
        query: 'getContact',
        clientId,
        request: { contactId: primaryContactId }
      })
      return data
    },
    enabled: !!primaryContactId
  })

  // Build the button list from the contact's household.members array.
  // Filter by the admin's configured `householdTitles` allow-list (empty
  // = show all). The primary contact is always the first button; other
  // household members follow.
  const buttons = useMemo(() => {
    const primary = primaryQuery.data
    if (!primary) return []

    const primaryButton = {
      id: `primary-${primary.id}`,
      contactId: primary.id,
      name: [primary.first_name, primary.last_name].filter(Boolean).join(' ') || primary.name || 'Primary Contact',
      title: primary.household?.title || null,
      isPrimary: true
    }

    const allowedTitles = config?.householdTitles || []
    const members = primary.household?.members || []
    const others = members
      .filter(m => m.id !== primary.id)
      .filter(m => allowedTitles.length === 0 || allowedTitles.includes(m.title))
      .map(m => ({
        id: `member-${m.id}`,
        contactId: m.id,
        name: [m.first_name, m.last_name].filter(Boolean).join(' ') || `Contact ${m.id}`,
        title: m.title || null,
        isPrimary: false
      }))

    return [primaryButton, ...others]
  }, [primaryQuery.data, config?.householdTitles])

  return {
    primaryContactId,
    buttons,
    isLoading: link.isLoading || primaryQuery.isFetching,
    notLinked: link.data?.linked === false,
    error: link.error || primaryQuery.error
  }
}

// Per-button detail fetcher used by the side panel when the user clicks a
// family-member button. Disabled by default; the panel passes
// `{ enabled: open }` so we don't pre-fetch every household member.
export function useWealthboxContactDetail (contactId, { enabled = true } = {}) {
  const { clientId, userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.wealthboxDomainQuery, 'contact-info-detail', userId, clientId, contactId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
        query: 'getContact',
        clientId,
        request: { contactId }
      })
      return data
    },
    enabled: enabled && !!contactId
  })
}
